import { BalanceData, Fix64, GearItem, GearOwner, GearStat, GearTarget, PlayerProfile, RARITY_IDS, Rarity, itemContribution, primaryOn, primaryValue, raiseCap, raiseCostAt, sameOwner } from "@highmarch/domain";
import { GearCompare, OwnerView, itemScore, loadoutOf } from "@highmarch/app";
import { RARITY_LABEL, fmtStat, gearIconFallback, pct } from "../gearUi";
import { icon } from "../iconsKit";
import { gearIconUrl, portraitUrl, resourceIconUrl, uiUrl } from "../portraits";
import { essenceIconUrl, sigilIconUrl } from "../forgeArt";
import type { ArmouryCtx } from "./host";
import { refusalWords } from "../hub/refusalWords";

/**
 * Shared bits of the Armoury's item surfaces (picker, item card, stash, Clear junk): DOM builders and the words and
 * chips every surface prints the same way. The panel mount is itemPanels.ts, the equip / upgrade path itemActions.ts,
 * the feedback itemFx.ts.
 */

// ---------------------------------------------------------------------------------------------------------------- DOM

type Kid = Node | string | null | undefined | false;

/** `document.createElement` with a class and children (strings become text); no HTML parsing, so no injection. */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", ...kids: Kid[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  for (const k of kids) {
    if (k === null || k === undefined || k === false) continue;
    if (typeof k === "string" && k.includes(BOLT)) appendBolted(e, k);
    else e.append(k);
  }
  return e;
}

/** The gear-power sign in copy; drawn as the kit's bolt icon (the emoji renders as a yellow glyph on iOS). */
export const BOLT = "⚡";
function appendBolted(e: HTMLElement, text: string): void {
  text.split(BOLT).forEach((part, i) => {
    if (i > 0) { const b = document.createElement("span"); b.className = "bolt"; b.setAttribute("aria-label", "gear"); b.innerHTML = icon("bolt", 12); e.append(b); }
    if (part) e.append(part);
  });
}

/** A kit icon (trusted, generated SVG markup) in its own span. */
export function ico(name: string, size: number, cls = "ic"): HTMLSpanElement {
  const s = h("span", cls);
  s.innerHTML = icon(name, size);
  return s;
}

export function btn(cls: string, ...kids: Kid[]): HTMLButtonElement {
  const b = h("button", cls, ...kids);
  b.type = "button";
  return b;
}

// -------------------------------------------------------------------------------------------------------------- words

export const rarityId = (it: Pick<GearItem, "rarity">): string => RARITY_IDS[it.rarity];
/** "Mount +1" (the rarity word is printed next to it in its colour). */
/** "Lv 23": an item's level (gear v2), the tag the "+N" of v1 was. */
export const levelTag = (it: Pick<GearItem, "level">): string => `Lv ${it.level}`;
export const slotTitle = (B: BalanceData, it: GearItem): string => `${B.gear.slots[it.slot].name} ${levelTag(it)}`;
/** "Rare Mount +1". */
export const fullTitle = (B: BalanceData, it: GearItem): string => `${RARITY_LABEL[it.rarity]} ${slotTitle(B, it)}`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Compare-table label for a stat ("Fire rate", "Base HP"). */
export const statLabel = (s: GearStat): string => cap(fmtStat(s));
/** Short stat word for one-line deltas ("dmg"). */
const STAT_SHORT = ["dmg", "range", "rate", "HP", "points"];
export const statShort = (s: GearStat): string => STAT_SHORT[s] ?? fmtStat(s);
/** "+35%"; zero reads as a dash in tables. */
export const pctOrDash = (f: Fix64): string => (f.isZero() ? "—" : pct(f));
/** A signed percent delta: "+14%", "−2%" (a real minus sign). */
export function signedPct(f: Fix64): string {
  if (f.isZero()) return "0";
  const s = pct(f.abs());
  return f.isNegative() ? `−${s.slice(1)}` : s;
}
export const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");

export const ownerIsBase = (o: GearOwner): boolean => o.target === GearTarget.Base;
export const ownerName = (B: BalanceData, o: GearOwner): string => (ownerIsBase(o) ? "Fortress" : B.towers[o.towerIndex]?.name ?? "?");
export const ownerArtId = (B: BalanceData, o: GearOwner): string => (ownerIsBase(o) ? B.towers[B.baseTowerIndex].id : B.towers[o.towerIndex]?.id ?? "");
export const ownerLevel = (p: PlayerProfile, o: GearOwner): number => (ownerIsBase(o) ? p.baseLevel : p.towerLevels[o.towerIndex]);

/** The NEW watermark as it was when the page opened (the shell marks everything seen on open). */
export function newSince(ctx: ArmouryCtx): number | undefined {
  return ctx.newSince ?? ctx.save.gearSeenUpTo;
}
export const isNew = (ctx: ArmouryCtx, it: GearItem): boolean => {
  const w = newSince(ctx);
  return w !== undefined && it.id > w;
};

/** The owners of the page's scope, as the shell lists them (seat = in the next level's line-up). */
export function ownerViews(ctx: ArmouryCtx): OwnerView[] { return loadoutOf(ctx.profile, ctx.scope); }
export function viewOf(views: readonly OwnerView[], o: GearOwner): OwnerView | undefined { return views.find((v) => sameOwner(v.owner, o)); }

/** Each stat an item gives on this owner: its primary (affinity included) and its affixes, as they count there (domain itemContribution). */
export function contribution(B: BalanceData, it: GearItem | null, owner: GearOwner): Fix64[] {
  return it === null ? [Fix64.Zero, Fix64.Zero, Fix64.Zero, Fix64.Zero, Fix64.Zero] : itemContribution(B, it, owner);
}

/** Whether putting the item on helps the owner: more gear power, or (equal power) more stats in total. */
export function helps(c: GearCompare): boolean {
  if (c.power !== 0) return c.power > 0;
  return c.stats.reduce((n, s) => n + (s.after.raw - s.before.raw), 0n) > 0n;
}

// ------------------------------------------------------------------------------------------------------------- pieces

/** The painted gear disc (falls back to the slot glyph if the PNG fails). */
export function gearImg(B: BalanceData, it: GearItem, size: number, cls = "gi"): HTMLImageElement {
  const slot = B.gear.slots[it.slot];
  const img = h("img", cls);
  img.decoding = "async";
  img.alt = "";
  img.src = gearIconUrl(slot.id, rarityId(it));
  img.width = size; img.height = size;
  gearIconFallback(img, slot.id, size);
  return img;
}

/** Big icon on its rarity glow with a +N pip (item card, mythic banner). */
export function gearDisc(B: BalanceData, it: GearItem, cls = "adisc"): HTMLDivElement {
  const glow = h("img", "glow");
  glow.alt = ""; glow.decoding = "async";
  glow.src = uiUrl(`chests/glow_${rarityId(it)}.png`);
  return h("div", `${cls} r${it.rarity}`, glow, gearImg(B, it, 92), h("span", "pl", levelTag(it)));
}

/** An owner's portrait in a navy frame. */
export function faceEl(B: BalanceData, o: GearOwner, cls = ""): HTMLDivElement {
  const url = portraitUrl(ownerArtId(B, o));
  const f = h("div", cls ? `face ${cls}` : "face");
  if (url) { const img = h("img"); img.alt = ""; img.decoding = "async"; img.src = url; f.append(img); }
  else f.append(ico(ownerIsBase(o) ? "shield" : "tower", 22));
  return f;
}

export const rarityTag = (it: GearItem): HTMLSpanElement => h("span", `rtag r${it.rarity}`, RARITY_LABEL[it.rarity].toUpperCase());
export const tag = (text: string, cls = ""): HTMLSpanElement => h("span", `atag${cls ? ` ${cls}` : ""}`, text);
export function tagIcon(iconName: string, text: string, cls = ""): HTMLSpanElement {
  return h("span", `atag${cls ? ` ${cls}` : ""}`, ico(iconName, 11), text);
}

/** ⚡ chip: green +n, red −n, neutral "=" — or, with `total`, the navy "⚡10" of what an item is worth. */
export function powerChip(n: number, total = false): HTMLSpanElement {
  const kind = total ? "eq" : n > 0 ? "up" : n < 0 ? "dn" : "same";
  return h("span", `pwchip ${kind}`, ico("bolt", 12), total ? String(n) : n === 0 ? "=" : signed(n));
}

function goldImg(): HTMLImageElement {
  const img = h("img", "res");
  img.alt = "gold"; img.decoding = "async";
  img.src = resourceIconUrl("gold");
  return img;
}

/** Forge Essence's painted flask as an `<img>` (gear v2). */
export function essImg(cls = "res"): HTMLImageElement {
  const img = h("img", cls);
  img.alt = "essence"; img.decoding = "async";
  img.src = essenceIconUrl();
  return img;
}

/** A Fusion Sigil's medallion as an `<img>`. */
export function sigilImg(rarity: Rarity, cls = "res"): HTMLImageElement {
  const img = h("img", cls);
  img.alt = `${RARITY_LABEL[rarity]} sigil`; img.decoding = "async";
  img.src = sigilIconUrl(rarity);
  return img;
}

/** "+162 [gold] +18 [essence]" in green: what the player GETS (never reads as a cost). */
export function gainEl(gold: number, cls = "again", essence = 0): HTMLSpanElement {
  const out = h("span", cls);
  if (gold > 0) out.append(h("span", "g au", `+${gold}`, goldImg()));
  if (essence > 0) out.append(h("span", "g ess", `+${essence}`, essImg()));
  return out;
}

/** A gold cost with its coins; red when the player is short of it. */
export function costEl(cost: number, have: number): HTMLSpanElement {
  const out = h("span", "acost");
  if (cost > 0) out.append(h("span", `c au${have < cost ? " short" : ""}`, goldImg(), String(cost)));
  return out;
}

/** "need 12 gold" (what is missing for a cost), or "" when it is affordable. */
export function shortfallText(cost: number, have: number): string {
  return have >= cost ? "" : `need ${cost - have} gold`;
}

/** An essence cost (gear v2's Forge Essence) with its flask; red when the player is short of it. */
export function essenceEl(cost: number, have: number): HTMLSpanElement {
  const out = h("span", "acost");
  if (cost > 0) {
    const c = h("span", `c ess${have < cost ? " short" : ""}`, essImg(), String(cost));
    c.setAttribute("aria-label", `${cost} essence`);
    out.append(c);
  }
  return out;
}

/** "need 12 essence", or "" when it is affordable. */
export function essenceShortfall(cost: number, have: number): string {
  return have >= cost ? "" : `need ${cost - have} essence`;
}

/** How many levels this much essence would raise an item (stops at its cap). */
export function levelsAffordable(profile: PlayerProfile, it: GearItem, budget: number): number {
  const B = profile.balance, cap = raiseCap(profile, it);
  let left = budget, n = 0;
  for (let level = it.level; level < cap; level++, n++) {
    const c = raiseCostAt(B, it.rarity, level);
    if (c > left) break;
    left -= c;
  }
  return n;
}

/**
 * What a salvage's Forge Essence buys (War room graft): the worn item — line-up owners first, then the strongest — that
 * the essence lets the player raise further than the essence held alone. "pays for Rare Mount Lv 24 on Ballista", or
 * null. `exclude`: items being salvaged (they cannot be raised afterwards).
 */
export function paysFor(ctx: ArmouryCtx, gain: number, exclude: ReadonlySet<number> = new Set()): string | null {
  const B = ctx.B, have = ctx.profile.forge.essence, budget = have + gain;
  let best: { it: GearItem; to: number; o: OwnerView; key: number } | null = null;
  for (const o of ownerViews(ctx)) {
    if (!o.owned) continue;
    for (const s of o.sockets) {
      const it = s.worn;
      if (it === null || exclude.has(it.id)) continue;
      const alone = levelsAffordable(ctx.profile, it, have), more = levelsAffordable(ctx.profile, it, budget);
      if (more <= alone) continue;
      // Line-up first (it counts for the next level), then the item worth most.
      const key = (o.seat ? 1_000_000 : 0) + itemScore(B, it) * 100 + (more - alone);
      if (best === null || key > best.key) best = { it, to: it.level + more, o, key };
    }
  }
  return best === null ? null : `pays for ${RARITY_LABEL[best.it.rarity]} ${B.gear.slots[best.it.slot].name} Lv ${best.to} on ${ownerName(B, best.o.owner)}`;
}

/** The primary this item would show one level up (on `owner` when given, affinity included). */
export function primaryAfterPlus(B: BalanceData, it: GearItem, owner: GearOwner | null): { now: Fix64; next: Fix64 } {
  const nextItem = { ...it, level: it.level + 1 };
  return owner === null ? { now: primaryValue(B, it), next: primaryValue(B, nextItem) } : { now: primaryOn(B, it, owner), next: primaryOn(B, nextItem, owner) };
}

/** Empty-state art for "clear levels to earn gear chests": the chest a win brings (the stash, the "For X" strip). */
export const EMPTY_CHEST_ART = "chests/chest_common_closed.png";
/** Glyph size for a ghosted slot icon's fallback (a little under the ring). */
const GHOST_GLYPH_PX = 34;

export function chestArt(): HTMLImageElement {
  const img = h("img", "chest");
  img.alt = ""; img.decoding = "async";
  img.src = uiUrl(EMPTY_CHEST_ART);
  return img;
}

/** A slot's own painted icon, greyed, in a soft dashed ring: "none of these yet" for that slot. */
export function slotGhost(B: BalanceData, slot: number, cls = ""): HTMLSpanElement {
  const def = B.gear.slots[slot];
  const img = h("img", "gi");
  img.alt = ""; img.decoding = "async";
  img.src = gearIconUrl(def.id, RARITY_IDS[Rarity.Common]);
  const ring = h("span", `aghost${cls ? ` ${cls}` : ""}`, img);
  gearIconFallback(img, def.id, GHOST_GLYPH_PX);
  return ring;
}

/** An empty state: art (the chest by default) over a line of text. */
export function emptyEl(text: string, art: Node = chestArt()): HTMLDivElement {
  return h("div", "aempty", art, h("p", "", text));
}

/** One socket kind's empty state (the picker): the slot's ghosted icon, its name, and a line. */
export function slotEmptyEl(B: BalanceData, slot: number, text: string): HTMLDivElement {
  return h("div", "aempty slot", slotGhost(B, slot), h("b", "disp", B.gear.slots[slot].name), h("p", "", text));
}

// ------------------------------------------------------------------------------------------------------- toast words

/** "+162 gold" (and ", +18 essence") for a toast. */
export function gainWords(gold: number, essence = 0): string {
  return [gold > 0 ? `+${gold} gold` : "", essence > 0 ? `+${essence} essence` : ""].filter(Boolean).join(", ");
}

/** The profile's refusal words, as a sentence for a toast. */
export function refusal(r: string): string {
  return refusalWords(r);
}
